import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Outlet, useParams } from 'react-router-dom';
import { Box, Typography, Paper } from '@mui/material';
import axios from '../utils/axiosConfig';
import { ValidationProvider } from '../context/ValidationContext';
import CompetitionImage from '../components/CompetitionImage';
import { handleApiError } from '../utils/apiErrorHandler';
import CompetitionInfoTabs from '../components/CompetitionInfoTabs';

import logger from '../utils/logger';

interface Competition {
  _id: string;
  name: string;
  location: string;
  startDate: string;
  endDate: string;
  registrationDeadline: string;
}

interface ValidationItem {
  status: boolean;
  message: string;
}

// Create a context to share validation state
export const CompetitionInfoContext = React.createContext<{
  notices: Record<string, ValidationItem>;
  refreshNotices: () => void;
}>({
  notices: {},
  refreshNotices: () => {}
});

const RegisteredCompetitionPage: React.FC = () => {
  const { competitionId } = useParams();
  const [competition, setCompetition] = useState<Competition | null>(null);
  const [loading, setLoading] = useState(true);
  const [infoNotices, setInfoNotices] = useState<Record<string, ValidationItem>>({});
  const [hasLogo, setHasLogo] = useState<boolean>(false);
  const [noticesVersion, setNoticesVersion] = useState(0);

  // Function to refresh notices/validations
  const refreshNotices = useCallback(async () => {
    if (competitionId) {
      try {
        // We could have an endpoint like this to get notices for registered competitions
        //const response = await axios.get(`/api/registered-competitions/${competitionId}/notices`);
        //setInfoNotices(response.data.notices || {});
        setInfoNotices({});
        setNoticesVersion(prev => prev + 1);
      } catch (error) {
        //console.log('No notices endpoint available yet, using mock data');
        logger.info('No notices endpoint available yet, using mock data', {error: error});
        // For now, we can simulate some notices for demonstration
        setInfoNotices({
          'summary': { status: false, message: '' },
          'cards': { status: true, message: 'Wettkampfkarten müssen bis zum 15.04.2025 abgegeben werden' },
          'schedule': { status: false, message: '' },
          'info': { status: true, message: 'Neue Informationen zum Wettkampf sind verfügbar' }
        });
        setNoticesVersion(prev => prev + 1);
      }
    }
  }, [competitionId]);

  // Memoize the context value
  const contextValue = useMemo(() => ({
    notices: infoNotices,
    refreshNotices
  }), [infoNotices, refreshNotices, noticesVersion]);

  useEffect(() => {
    // Fetch competition details
    if (competitionId) {
      setLoading(true);
      axios.get(`/api/competitions/${competitionId}`)
        .then(response => {
          setCompetition(response.data);
          // Check if logo exists
          axios.head(`/api/images/Competition/${competitionId}`)
            .then(() => setHasLogo(true))
            .catch(() => setHasLogo(false));
          setLoading(false);
        })
        .catch(error => {
          handleApiError(error, 'Fehler beim Laden der Wettkampfdaten.');
          setLoading(false);
        });
    }
  }, [competitionId]);

  // Initial notices fetch
  useEffect(() => {
    if (competitionId) {
      refreshNotices();
    }
  }, [competitionId, refreshNotices]);

  if (loading) {
    return <Typography>Laden...</Typography>;
  }

  if (!competition) {
    return <Typography color="error">Wettkampf nicht gefunden</Typography>;
  }

  return (
    <Box>
      <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          <Box>
            <CompetitionImage 
              competitionId={competition._id} 
              hasImage={hasLogo}
              imageType="logo"
              size="large"
              useFullSize={true}
            />
          </Box>
          <Box>
            <Typography variant="h4" gutterBottom>
              {competition.name}
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              {competition.location} | {new Date(competition.startDate).toLocaleDateString()} - {new Date(competition.endDate).toLocaleDateString()}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Meldeschluss war am: {new Date(competition.registrationDeadline).toLocaleDateString()}
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Information tabs - showing sections for the registered competition */}
      <CompetitionInfoTabs 
        key={`info-tabs-${noticesVersion}`}
        notices={infoNotices} 
      />
      
      {/* Provide the validation context to all child components */}
      <CompetitionInfoContext.Provider value={contextValue}>
        <ValidationProvider>
          <Box sx={{ mt: 3 }}>
            <Outlet />
          </Box>
        </ValidationProvider>
      </CompetitionInfoContext.Provider>
    </Box>
  );
};

export default RegisteredCompetitionPage;